import React, { useState, useEffect } from 'react';
import axios from 'axios';

const TempMail = () => {
  const [publicTempMail, setPublicTempMail] = useState(null);
  const [userAliases, setUserAliases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [selectedAlias, setSelectedAlias] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  // Create public temp mail (no auth required)
  const createPublicTempMail = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/tempmail/public/create');
      setPublicTempMail(response.data);
      // Start polling for emails
      pollInbox(response.data.id);
    } catch (err) {
      setError('Failed to create temp mail');
    } finally {
      setLoading(false);
    }
  };

  // Create alias for logged-in user
  const createAlias = async () => {
    if (!isLoggedIn) {
      setError('Please log in to create aliases');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/tempmail/alias/create', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAliases([response.data, ...userAliases]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create alias');
    } finally {
      setLoading(false);
    }
  };

  // Get user's aliases
  const fetchAliases = async () => {
    if (!isLoggedIn) return;
    try {
      const response = await axios.get('/api/tempmail/aliases', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAliases(response.data || []);
    } catch (err) {
      console.error('Failed to fetch aliases:', err);
    }
  };

  // Poll inbox for public temp mail
  const pollInbox = async (id) => {
    const checkInbox = async () => {
      try {
        const response = await axios.get(`/api/tempmail/public/inbox/${id}`);
        setInbox(response.data.emails || []);
      } catch (err) {
        console.error('Inbox poll error:', err);
      }
    };
    
    checkInbox();
    const interval = setInterval(checkInbox, 10000); // Check every 10s
    return () => clearInterval(interval);
  };

  useEffect(() => {
    fetchAliases();
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="fade-in" style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '16px' }}>
          📧 Temp Mail
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--text-muted)' }}>
          Create disposable email addresses to protect your privacy
        </p>
      </div>

      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #EF4444', color: '#EF4444', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Public Temp Mail Section */}
      <div className="card" style={{ padding: '30px', marginBottom: '30px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>
          🕐 Temporary Email (24 Hours)
        </h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
          Create a disposable email address instantly. No registration required. 
          Emails are stored for 24 hours and then deleted.
        </p>

        {!publicTempMail ? (
          <button
            className="btn btn-primary"
            onClick={createPublicTempMail}
            disabled={loading}
            style={{ width: '100%', padding: '16px' }}
          >
            {loading ? '⏳ Creating...' : '📧 Create Temp Email'}
          </button>
        ) : (
          <div>
            <div style={{
              background: 'var(--darker)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                Your temporary email address:
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <code style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  background: 'var(--card)',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  wordBreak: 'break-all'
                }}>
                  {publicTempMail.email_address}
                </code>
                <button
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(publicTempMail.email_address)}
                >
                  {copied ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>
              <p style={{ fontSize: '12px', color: '#F59E0B', marginTop: '12px' }}>
                ⏰ Expires: {formatTime(publicTempMail.expires_at)}
              </p>
            </div>

            {/* Inbox */}
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              📬 Inbox ({inbox.length})
            </h3>
            {inbox.length === 0 ? (
              <div style={{
                background: 'var(--darker)',
                border: '1px dashed var(--border)',
                borderRadius: '8px',
                padding: '30px',
                textAlign: 'center',
                color: 'var(--text-muted)'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📭</div>
                <p>No emails yet</p>
                <p style={{ fontSize: '12px' }}>Waiting for incoming messages...</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {inbox.map((email, idx) => (
                  <div key={idx} style={{
                    background: 'var(--darker)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '14px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontWeight: '600' }}>{email.from}</span>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {formatTime(email.received_at)}
                      </span>
                    </div>
                    <div style={{ fontSize: '14px', marginBottom: '6px' }}>{email.subject}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      {email.body?.slice(0, 100)}...
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Aliases Section */}
      <div className="card" style={{ padding: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700' }}>
              🎭 Your Email Aliases
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
              {isLoggedIn ? 'Aliases forward to your main inbox' : 'Log in to create persistent aliases'}
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={createAlias}
            disabled={loading}
          >
            ➕ Create Alias
          </button>
        </div>

        {!isLoggedIn ? (
          <div style={{
            background: 'var(--darker)',
            border: '1px dashed var(--border)',
            borderRadius: '8px',
            padding: '30px',
            textAlign: 'center'
          }}>
            <p style={{ color: 'var(--text-muted)' }}>
              <a href="/login" style={{ color: '#DC2626' }}>Log in</a> or{' '}
              <a href="/register" style={{ color: '#DC2626' }}>register</a> to create 
              persistent email aliases that forward to your inbox.
            </p>
          </div>
        ) : userAliases.length === 0 ? (
          <div style={{
            background: 'var(--darker)',
            border: '1px dashed var(--border)',
            borderRadius: '8px',
            padding: '30px',
            textAlign: 'center',
            color: 'var(--text-muted)'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>🎭</div>
            <p>No aliases yet</p>
            <p style={{ fontSize: '12px' }}>Create your first alias above</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {userAliases.map((alias) => (
              <div key={alias.id} style={{
                background: 'var(--darker)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <code style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    background: 'var(--card)',
                    padding: '6px 10px',
                    borderRadius: '4px'
                  }}>
                    {alias.alias_address}
                  </code>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                    ➜ Forwards to: {alias.forwards_to}
                  </div>
                  <div style={{ fontSize: '12px', color: '#F59E0B', marginTop: '4px' }}>
                    ⏰ Expires: {formatTime(alias.expires_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => copyToClipboard(alias.alias_address)}
                  >
                    📋
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TempMail;

